<template>
  <div>
    <DashboardHeader
      heading="Contact requests"
      paragraph="View all of the businesses that have submitted an agency contact request"
    />

    <template v-if="loading">
      <Logo />
    </template>
    <template v-else>
      <TableToolbar :query="filters.name.value" @update-query="filterRows">
        <div v-if="leadFlowEnabled">
          <label style="margin-bottom: 0">
            <div class="select select-mb-0">
              <select
                @change="
                  filterRows(
                    ['projectStatus'],
                    $event.target.value,
                    'projectStatus'
                  )
                "
              >
                <option selected="selected" value>Project status</option>
                <option
                  :selected="filters.projectStatus.value === 'N'"
                  value="N"
                  >New lead</option
                >
                <option
                  :selected="filters.projectStatus.value === 'C'"
                  value="C"
                  >Lead contacted</option
                >
                <option
                  :selected="filters.projectStatus.value === 'L'"
                  value="L"
                  >Lead lost</option
                >
                <option
                  :selected="filters.projectStatus.value === 'S'"
                  value="S"
                  >Project secured</option
                >
              </select>
            </div>
          </label>
        </div>
      </TableToolbar>
      <div class="mb-lg">
        <v-table
          :data="rows"
          :filters="filters"
          :currentPage.sync="currentPage"
          :pageSize="pageSize"
          class="table table-bordered"
          @totalPagesChanged="totalPages = $event"
          :totalItemsInput="count"
        >
          <thead slot="head">
            <v-th
              v-on:sortChange="sortChange"
              v-for="(column, i) in columns"
              :key="i"
              :sortKey="column.name"
              class="mb-xxs grey-background fs-sm"
              >{{ column.displayName }}</v-th
            >
          </thead>
          <tbody
            slot="body"
            slot-scope="{ displayData }"
            v-if="displayData.length"
          >
            <tr
              v-for="(row, i) in displayData"
              :key="i"
              @click="
                $helpers.navigateOrNewTab(
                  `/contact-requests/${row.id}`,
                  $router,
                  $event
                )
              "
            >
              <td class="fs-sm f-capitalize fw-semibold">
                {{ row.companyName }}
              </td>
              <td class="fs-sm f-capitalize">{{ row.resellerName }}</td>
              <td class="fs-sm f-capitalize">{{ showDate(row.created) }}</td>
              <td class="fs-sm" v-if="agencyLeadsEnabled">
                <i
                  class="fa fa-circle mr-xs"
                  :class="row.status | circleClass"
                />
                {{ row.status | status }}
              </td>
              <td v-if="leadFlowEnabled" class="fs-sm f-capitalize">
                <span>{{ row.projectStatus | projectStatus }}</span>
              </td>
            </tr>
          </tbody>
          <tbody
            style="padding: 1em; display: block;"
            class="fs-sm fw-semibold fc-medium"
            v-else
          >
            No contact requests!
          </tbody>
        </v-table>
        <smart-pagination
          :currentPage.sync="currentPage"
          :count="count"
          :pageSize="pageSize"
          :totalPages="Math.ceil(count / pageSize)"
          v-on:update:currentPage="pageChanged($event)"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { formatDate } from '@/utils/date';
import DashboardHeader from '@/components/Dashboard/DashboardHeader';
import TableToolbar from '@/components/Dashboard/TableToolbar';
import Logo from '@/components/SpinningLogo';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters, mapActions, mapState } = createNamespacedHelpers(
  'contactRequests/table'
);

export default {
  name: 'ContactRequests',
  components: {
    TableToolbar,
    DashboardHeader,
    Logo,
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 3,
    };
  },
  filters: {
    status(value) {
      switch (value) {
        case 'P':
          return 'Pending';
        case 'A':
          return 'Accepted';
        case 'R':
          return 'Declined';
      }
    },
    projectStatus(value) {
      switch (value) {
        case 'N':
          return 'New lead';
        case 'C':
          return 'Lead contacted';
        case 'L':
          return 'Lead lost';
        case 'S':
          return 'Project secured';
        default:
          return '-';
      }
    },
    circleClass(value) {
      switch (value) {
        case 'P':
          return 'fc-light';
        case 'A':
          return 'fc-green';
        case 'R':
          return 'fc-alert';
      }
    },
  },
  created() {
    this.fetchContactData(1);
  },
  computed: {
    ...mapGetters({
      rawColumns: 'getColumns',
      rows: 'getRows',
      filters: 'getFilters',
    }),
    ...mapState(['count', 'pageSize', 'loading']),
    columns() {
      if (this.leadFlowEnabled && this.agencyLeadsEnabled) {
        let statusIndex = this.rawColumns.findIndex(element => {
          return element.name === 'status';
        });
        this.setDisplayName(statusIndex, 'Lead');
        return this.rawColumns;
      } else if (this.agencyLeadsEnabled) {
        return this.rawColumns.filter(col => {
          return col.name !== 'projectStatus';
        });
      } else if (this.leadFlowEnabled) {
        let statusIndex = this.rawColumns.findIndex(element => {
          return element.name === 'status';
        });
        this.setDisplayName(statusIndex, 'Lead');
        return this.rawColumns.filter(col => {
          return col.name !== 'status';
        });
      }

      return this.rawColumns.filter(col => {
        if (col.name === 'status') return false;
        if (col.name === 'projectStatus') return false;
        return true;
        // return col.name !== "status" || col.name !== "projectStatus";
      });
    },
    agencyLeadsEnabled() {
      return this.$store.getters['details'].agencyLeadViewEnabled;
    },
    leadFlowEnabled() {
      return this.$store.getters['details'].leadFlowEnabled;
    },
  },
  methods: {
    ...mapActions(['updateQueryAndFilter', 'fetchContactData', 'sortResults']),
    sortChange(key) {
      this.sortResults(key);
      this.currentPage = 1;
    },
    pageChanged(page) {
      this.fetchContactData(page);
    },

    filterRows(fields, value, type) {
      fields = fields || ['resellerName', 'companyName'];
      this.currentPage = 1;
      this.updateQueryAndFilter({ fields, value, type });
    },
    showDate(date) {
      if (date) {
        return formatDate(new Date(String(date)));
      }
      return '-';
    },
    setDisplayName(index, name) {
      this.rawColumns[index].displayName = name;
    },
  },
};
</script>
